<template>
  <div class="gc-wo-bulk-update-step-apply">
    <!-- Top warning banner -->
    <div class="wap-top-banner">
      ⚠ You are about to apply changes to
      <strong>{{ wosToUpdate }} Work Order{{ wosToUpdate !== 1 ? "s" : "" }}</strong>.
      This action cannot be undone.
    </div>

    <div class="wap-body-layout">
      <!-- ── Main column ─────────────────────────────────────────────────────── -->
      <div class="wap-main">
        <!-- Stale diff banner — inline in Apply step, mirrors Step 3 -->
        <GcWoBulkUpdateStaleDiffBanner
          v-if="staleDiffWarningActive && diffComputedAt"
          :affected-wo-count="staleDiffAffectedWoCount"
          :detail="staleDiffDetail"
          :stale-wos="staleWos"
          :is-read-only="isReadOnly"
          @recompute="$emit('stale-diff-recompute')"
          @acknowledge="$emit('stale-diff-acknowledge', $event)"
        />

        <!-- Summary metric cards -->
        <div class="wap-metrics">
          <div class="wap-metric-card">
            <span class="wap-metric-label">Work Orders to update</span>
            <span class="wap-metric-val wap-val-blue">{{ wosToUpdate }}</span>
          </div>
          <div class="wap-metric-card">
            <span class="wap-metric-label">Material lines to change</span>
            <span class="wap-metric-val wap-val-amber">{{
              materialLinesToChange
            }}</span>
          </div>
          <div class="wap-metric-card">
            <span class="wap-metric-label">Work Orders excluded</span>
            <span
              :class="[
                'wap-metric-val',
                wosExcluded > 0 ? 'wap-val-red' : '',
              ]"
              >{{ wosExcluded }}</span
            >
          </div>
        </div>

        <!-- Confirm box -->
        <div
          :class="['wap-confirm-box', { 'wap-confirm-box-active': confirmReady }]"
        >
          <div class="wap-confirm-instruction">
            Type <strong>CONFIRM</strong> to apply changes to
            <strong>{{ wosToUpdate }} Work Order{{ wosToUpdate !== 1 ? "s" : "" }}</strong>.
          </div>
          <div v-if="staleDiffWarningActive" class="wap-stale-guard-note">
            ⛔ Resolve the stale diff warning above before applying.
          </div>
          <div class="wap-confirm-actions">
            <div class="wap-confirm-input-wrap">
              <input
                v-model="confirmText"
                :class="['wap-confirm-input', 'wap-confirm-input--' + confirmState]"
                placeholder="CONFIRM"
                :disabled="isReadOnly || applying"
                autocomplete="off"
                spellcheck="false"
              />
              <span v-if="confirmState === 'wrong'" class="wap-confirm-hint">
                Must match exactly: CONFIRM (case-sensitive)
              </span>
            </div>
            <DxButton
              :text="applying ? 'Processing...' : 'Proceed'"
              type="default"
              styling-mode="contained"
              class="wap-apply-btn"
              :disabled="!canApply || isReadOnly || applying"
              @click="onApplyClick"
            />
            <DxButton
              text="← Back to preview"
              type="normal"
              styling-mode="outlined"
              :disabled="applying"
              @click="$emit('go-back')"
            />
          </div>
        </div>
      </div>

      <!-- ── Sidebar ─────────────────────────────────────────────────────────── -->
      <div class="wap-sidebar">
        <div class="wap-sidebar-card">
          <div class="wap-sidebar-header">
            <span class="wap-sidebar-title">Apply summary</span>
          </div>
          <div class="wap-sidebar-body">
            <div class="wap-sidebar-row">
              <span class="wap-sidebar-lbl">Work Orders to update</span>
              <span class="wap-sidebar-val wap-val-blue">{{ wosToUpdate }}</span>
            </div>
            <div class="wap-sidebar-row">
              <span class="wap-sidebar-lbl">Work Orders excluded</span>
              <span
                :class="['wap-sidebar-val', wosExcluded > 0 ? 'wap-val-red' : '']"
                >{{ wosExcluded }}</span
              >
            </div>
            <div class="wap-sidebar-row">
              <span class="wap-sidebar-lbl">Material lines to change</span>
              <span class="wap-sidebar-val wap-val-amber">{{
                materialLinesToChange
              }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import DxButton from "devextreme-vue/button";
import GcWoBulkUpdateStaleDiffBanner from "./gc-wo-bulk-update-stale-diff-banner.vue";

export default {
  name: "GcWoBulkUpdateStepApply",
  components: { DxButton, GcWoBulkUpdateStaleDiffBanner },
  props: {
    active: { type: Boolean, default: false },
    wosToUpdate: { type: Number, default: 0 },
    wosExcluded: { type: Number, default: 0 },
    materialLinesToChange: { type: Number, default: 0 },
    isReadOnly: { type: Boolean, default: false },
    applying: { type: Boolean, default: false },
    // Stale diff
    staleDiffWarningActive: { type: Boolean, default: false },
    staleDiffAffectedWoCount: { type: Number, default: 0 },
    staleDiffDetail: { type: String, default: null },
    staleWos: { type: Array, default: () => [] },
    diffComputedAt: { type: String, default: null },
  },
  data() {
    return {
      confirmText: "",
    };
  },
  computed: {
    confirmState() {
      if (!this.confirmText) return "empty";
      return this.confirmText === "CONFIRM" ? "correct" : "wrong";
    },
    confirmReady() {
      return this.confirmState === "correct";
    },
    canApply() {
      return this.confirmReady && !this.staleDiffWarningActive;
    },
  },
  watch: {
    active(val) {
      if (!val) {
        this.confirmText = "";
      }
    },
  },
  methods: {
    onApplyClick() {
      if (!this.canApply || this.applying) return;
      this.$emit("apply");
    },
  },
};
</script>

<style lang="scss">
.gc-wo-bulk-update-step-apply {
  display: flex;
  flex-direction: column;
  gap: 16px;

  // ── Top warning banner ────────────────────────────────────────────────────────
  .wap-top-banner {
    padding: 12px 16px;
    background: #fff8f0;
    border: 1px solid #ffd6a5;
    border-radius: 6px;
    font-size: 13px;
    color: #7c4b00;
  }

  // ── Body layout — main + sidebar ──────────────────────────────────────────────
  .wap-body-layout {
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .wap-main {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .wap-sidebar {
    width: 240px;
    flex-shrink: 0;
  }

  // ── Metric cards ──────────────────────────────────────────────────────────────
  .wap-metrics {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 10px;
  }

  .wap-metric-card {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 14px 16px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .wap-metric-label {
    font-size: 11px;
    font-weight: 500;
    color: #6c757d;
    text-transform: uppercase;
    letter-spacing: 0.4px;
  }

  .wap-metric-val {
    font-size: 26px;
    font-weight: 700;
    color: #101828;
    line-height: 1;
  }

  // ── Colour values ─────────────────────────────────────────────────────────────
  .wap-val-blue {
    color: #0369a1;
  }
  .wap-val-amber {
    color: #92400e;
  }
  .wap-val-red {
    color: #9b1c1c;
  }

  // ── Confirm box ───────────────────────────────────────────────────────────────
  .wap-confirm-box {
    padding: 16px;
    background: #fff5f5;
    border: 1px solid #f5c6cb;
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    transition: background 0.15s, border-color 0.15s;

    &.wap-confirm-box-active {
      background: #f0fdf4;
      border-color: #86efac;
    }
  }

  .wap-confirm-instruction {
    font-size: 13px;
    font-weight: 500;
    color: #9b1c1c;

    .wap-confirm-box-active & {
      color: #065f46;
    }
  }

  .wap-stale-guard-note {
    font-size: 12px;
    color: #9b1c1c;
    background: #fff3f3;
    border: 1px solid #f5c6cb;
    border-radius: 4px;
    padding: 8px 12px;
  }

  .wap-confirm-actions {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }

  .wap-confirm-input-wrap {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  .wap-confirm-input {
    width: 160px;
    padding: 7px 10px;
    border: 1px solid #ced4da;
    border-radius: 4px;
    font-size: 14px;
    font-family: monospace;
    letter-spacing: 0.08em;
    color: #101828;
    background: #fff;

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(3, 169, 244, 0.15);
    }

    &--empty {
      border-color: #ced4da;
    }

    &--wrong {
      border-color: #dc3545;
      background: #fff8f8;

      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.15);
      }
    }

    &--correct {
      border-color: #17b26a;
      background: #f0fdf4;

      &:focus {
        box-shadow: 0 0 0 2px rgba(23, 178, 106, 0.15);
      }
    }

    &:disabled {
      background: #f8f9fa;
      color: #6c757d;
    }
  }

  .wap-confirm-hint {
    font-size: 11px;
    color: #dc3545;
  }

  // ── Sidebar card ──────────────────────────────────────────────────────────────
  .wap-sidebar-card {
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
  }

  .wap-sidebar-header {
    padding: 10px 14px;
    border-bottom: 1px solid #dee2e6;
    background: #f8f9fa;
    border-radius: 6px 6px 0 0;
  }

  .wap-sidebar-title {
    font-size: 12px;
    font-weight: 600;
    color: #344054;
  }

  .wap-sidebar-body {
    padding: 10px 14px;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .wap-sidebar-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  .wap-sidebar-lbl {
    font-size: 12px;
    color: #495057;
  }

  .wap-sidebar-val {
    font-size: 14px;
    font-weight: 700;
    color: #101828;
  }
}
</style>
